import Form from "@/components/Form";
import { List } from "@/components/list/List";
import SaveButton from "@/components/SaveButton";
import { useEffect, useCallback } from "react";
import { CostsFormSchemaType } from "@/schemas/costsForm";
import { Button, ButtonShortcut } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";
import ModKeyChar from "@/components/ModKeyChar";
import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";

interface CostsFormProps {
  onIncludeChange?: (costs: CostsFormSchemaType["costs"]) => void;
  onEdit: (
    values: CostsFormSchemaType["costs"],
    dirtyFields: Record<string, unknown>,
  ) => Promise<void>;
  onAddClick?: () => void;
  form: UseFormReturn<CostsFormSchemaType>;
  defaultValues: CostsFormSchemaType;
}

export const CostsForm = ({
  onIncludeChange,
  onEdit,
  onAddClick,
  form,
  defaultValues,
}: CostsFormProps) => {
  const { t } = useTranslation();
  const handleFormSubmit = useCallback(
    async (values: CostsFormSchemaType) => {
      await onEdit(values.costs, form.formState.dirtyFields.costs || {});
    },
    [onEdit, form.formState.dirtyFields],
  );

  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (name?.endsWith(".include") && values?.costs) {
        onIncludeChange?.(values.costs as CostsFormSchemaType["costs"]);
      }
    });

    return () => subscription.unsubscribe();
  }, [form, onIncludeChange]);

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  return (
    <Form form={form} handleSubmit={handleFormSubmit} className="h-full gap-1">
      <List.Content>
        <List.Status>
          <Button
            size="sm"
            variant="secondary"
            disabled={false} // TODO
            onClick={onAddClick}
          >
            <PlusIcon className="w-4 h-4" />
            {t("costs.buttons.addCost")}
            <ButtonShortcut>
              <ModKeyChar />A
            </ButtonShortcut>
          </Button>
          <List.DirtyRowsStatus />
        </List.Status>
      </List.Content>
      <footer className="flex p-2 justify-end gap-6">
        <SaveButton />
      </footer>
    </Form>
  );
};
